import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";

interface BeforeAfterSliderProps {
  before: ReactNode;
  after: ReactNode;
  beforeLabel: ReactNode;
  afterLabel: ReactNode;
  /** Divider position (percent from the left) the intro sweep settles on. */
  settleAt?: number;
  className?: string;
}

const clamp = (n: number) => Math.min(100, Math.max(0, n));

/**
 * Two full-size scenes stacked in one frame, with a draggable divider: the
 * "before" shows left of it, the "after" right of it. Scrolling it into
 * view plays one sweep (before → mostly after → settle) so it's obviously
 * draggable; reduced motion skips straight to the settle point.
 *
 * Only the handle drags, so either scene can hold its own interactive
 * content (a page can't intercept gestures inside an iframe, for one). The
 * clip also clips hit-testing, so hidden parts of the "after" scene can't
 * be clicked.
 */
export const BeforeAfterSlider = ({
  before,
  after,
  beforeLabel,
  afterLabel,
  settleAt = 40,
  className = "",
}: BeforeAfterSliderProps) => {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(100);
  const [dragging, setDragging] = useState(false);
  const sweep = useRef<{ stop: () => void } | null>(null);
  /** Set once the visitor moves the divider, so the intro never overrides them. */
  const touched = useRef(false);
  const reducedMotion = useReducedMotion();
  // Fire once the frame's top clears the lower 30% of the viewport. A margin
  // rather than a visible-fraction threshold, so it still fires when the
  // frame is taller than a short (e.g. landscape phone) viewport.
  const inView = useInView(frameRef, {
    once: true,
    margin: "0px 0px -30% 0px",
  });

  const stopIntro = () => {
    touched.current = true;
    sweep.current?.stop();
  };

  const moveTo = (target: number, duration = 0.6) => {
    stopIntro();
    if (reducedMotion) {
      setPos(target);
      return;
    }
    sweep.current = animate(pos, target, {
      duration,
      ease: [0.33, 1, 0.68, 1],
      onUpdate: setPos,
    });
  };

  useEffect(() => {
    if (!inView || touched.current) return;
    if (reducedMotion) {
      setPos(settleAt);
      return;
    }
    sweep.current = animate(100, [100, 12, settleAt], {
      duration: 2.2,
      times: [0, 0.6, 1],
      ease: "easeInOut",
      delay: 0.3,
      onUpdate: setPos,
    });
    return () => sweep.current?.stop();
    // Only the first time it comes into view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const posFromPointer = (clientX: number) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return pos;
    return clamp(((clientX - rect.left) / rect.width) * 100);
  };

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    stopIntro();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setPos(posFromPointer(e.clientX));
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (dragging) setPos(posFromPointer(e.clientX));
  };
  const endDrag = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDragging(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const step = e.shiftKey ? 20 : 5;
    const next =
      e.key === "ArrowLeft" || e.key === "ArrowDown"
        ? pos - step
        : e.key === "ArrowRight" || e.key === "ArrowUp"
          ? pos + step
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? 100
              : null;
    if (next === null) return;
    e.preventDefault();
    stopIntro();
    setPos(clamp(next));
  };

  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden rounded-lg select-none ${className}`}
    >
      <div className="absolute inset-0">{before}</div>
      <div
        className={`absolute inset-0 ${dragging ? "pointer-events-none" : ""}`}
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
      >
        {after}
      </div>

      {/* Scene labels double as snap buttons. */}
      <button
        type="button"
        onClick={() => moveTo(100)}
        className="absolute top-4 left-4 md:top-6 md:left-6 z-20 text-fwd-mid text-[0.65rem] md:text-xs font-bold tracking-[0.18em] uppercase bg-fwd-soft/90 rounded px-2.5 py-1.5 transition-opacity"
        style={{ opacity: pos > 12 ? 1 : 0.35 }}
      >
        {beforeLabel}
      </button>
      <button
        type="button"
        onClick={() => moveTo(0)}
        className="absolute top-4 right-4 md:top-6 md:right-6 z-20 text-fwd-yellow text-[0.65rem] md:text-xs font-bold tracking-[0.18em] uppercase bg-fwd-navy/90 rounded px-2.5 py-1.5 transition-opacity"
        style={{ opacity: pos < 88 ? 1 : 0.35 }}
      >
        {afterLabel}
      </button>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 z-10 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_12px_rgba(17,17,24,0.35)] pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      <button
        type="button"
        role="slider"
        aria-label="Compare the PDF with the narrative"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-valuetext={`${Math.round(pos)}% PDF, ${100 - Math.round(pos)}% narrative`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        className="absolute top-1/2 z-20 w-12 h-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white text-fwd-ink shadow-[0_4px_20px_rgba(17,17,24,0.35)] flex items-center justify-center cursor-ew-resize touch-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fwd-red"
        style={{ left: `${pos}%` }}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 6l-6 6 6 6M15 6l6 6-6 6" />
        </svg>
      </button>
    </div>
  );
};
